import { useCallback } from 'react';

import type { AxiosError } from 'axios';

/**
 * 에러 케이스 정의 타입
 * @description
 * - condition: 에러 조건을 검사하는 함수
 * - action: 조건이 매칭될 때 실행할 함수
 * - stopOnMatch: 매칭 시 후속 케이스 검사를 중단할지 여부 (기본값: true)
 * @example
 * const errorHandler = useErrorHandler({
 *   noToken: {
 *     condition: (error, context) => !context.token,
 *     action: (error, context) => enqueueToast('권한이 없습니다.'),
 *     stopOnMatch: true
 *   },
 * });
 */
type ErrorCase<TError extends AxiosError, TContext = any> = {
  /**
   * 조건이 매칭될 때 실행할 함수
   */
  action: (error: TError, context?: TContext) => void;
  /**
   * 에러 조건을 검사하는 함수
   */
  condition: (error: TError, context?: TContext) => boolean;
  /**
   * 매칭 시 후속 케이스 검사를 중단할지 여부 (기본값: true)
   */
  stopOnMatch?: boolean;
};

/**
 * 에러 핸들러 설정 타입
 */
type ErrorHandlerConfig<TError extends AxiosError, TContext = any> = Record<
  string,
  ErrorCase<TError, TContext>
>;

/**
 * 에러 핸들러 반환 타입
 */
type ErrorHandler<TError extends AxiosError, TContext = any> = {
  handle: (error: TError, context?: TContext) => void;
};

/**
 * 순서가 보장된 에러 핸들링을 제공하는 훅
 * @description
 * - key:object 형태로 에러 케이스들을 정의하여 전달
 * - 정의된 순서대로 조건을 검사하고 매칭되는 케이스 실행
 * - stopOnMatch 설정에 따라 후속 케이스 검사 중단 가능
 * @example
 * // 에러 핸들러 정의
 * const errorHandler = useErrorHandler({
 *   noToken: {
 *     condition: (error, context) => !context.token,
 *     action: (error, context) => enqueueToast('권한이 없습니다.'),
 *     stopOnMatch: true
 *   },
 *   unauthorized: {
 *     condition: (error) => error.response?.status === 401,
 *     action: () => enqueueToast('인증 실패'),
 *   }
 * });
 *
 * // 사용
 * catch (error) {
 *   errorHandler.handle(error, { token, setError });
 * }
 */
const useErrorHandler = <TError extends AxiosError, TContext = any>(
  config: ErrorHandlerConfig<TError, TContext>,
): ErrorHandler<TError, TContext> => {
  const handle = useCallback(
    (error: TError, context?: TContext) => {
      const entries = Object.entries(config);

      for (const [_, errorCase] of entries) {
        const { condition, action, stopOnMatch = true } = errorCase;

        if (condition(error, context)) {
          action(error, context);

          if (stopOnMatch) {
            break;
          }
        }
      }
    },
    [config],
  );

  return { handle };
};

export default useErrorHandler;
